<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :closable="false"
    :width="520"
    centered
    class="edit-member-modal"
    @cancel="handleClose"
  >
    <div class="modal-body">
      <!-- Header -->
      <div class="modal-header">
        <span class="modal-title">Ubah Profil Member</span>
        <close-outlined class="close-btn" @click="handleClose" />
      </div>

      <!-- Form -->
      <div class="form-fields">
        <!-- Nama -->
        <div class="field-group">
          <label class="field-label"
            >Nama Lengkap Tanpa Gelar <span class="required">*</span></label
          >
          <a-input v-model:value="form.name" placeholder="Masukkan nama" class="field-input" />
        </div>

        <!-- Gelar Depan -->
        <div class="field-group">
          <label class="field-label">Gelar Depan</label>
          <div class="gelar-depan-options">
            <button
              v-for="opt in gelarDepanOptions"
              :key="opt"
              class="gelar-depan-chip"
              :class="{ selected: form.gelarDepanSelected.includes(opt) }"
              type="button"
              @click="toggleGelarDepan(opt)"
            >
              <span class="chip-check">
                <svg v-if="form.gelarDepanSelected.includes(opt)" width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path d="M2 5.5L4.5 8L9 3" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              {{ opt }}
            </button>
          </div>
        </div>

        <!-- Gelar Belakang -->
        <div class="field-group">
          <label class="field-label">Gelar Belakang</label>
          <div v-for="(_, idx) in form.gelarBelakangList" :key="'gb-' + idx" class="list-row">
            <a-input
              :value="form.gelarBelakangList[idx]"
              placeholder="Contoh: Sp.JP"
              class="field-input"
              @change="e => (form.gelarBelakangList[idx] = e.target.value)"
            />
            <button class="btn-icon-only" @click="removeGelarBelakang(idx)">
              <delete-outlined />
            </button>
          </div>
          <button class="btn-tambah" @click="addGelarBelakang"><plus-outlined /> TAMBAH</button>
        </div>

        <!-- Subspesialis -->
        <div class="field-group">
          <label class="field-label">Subspesialis <span class="required">*</span></label>
          <div v-for="(_, idx) in form.subspesialisList" :key="'sp-' + idx" class="list-row">
            <a-select
              :value="form.subspesialisList[idx]"
              placeholder="Pilih subspesialis"
              class="field-select"
              :options="subspecialtyOptions"
              @change="val => (form.subspesialisList[idx] = val)"
            />
            <button class="btn-icon-only" @click="removeSubspesialis(idx)">
              <delete-outlined />
            </button>
          </div>
          <button
            v-if="form.subspesialisList.length < 2"
            class="btn-tambah"
            @click="addSubspesialis"
          >
            <plus-outlined /> TAMBAH
          </button>
        </div>

        <!-- Rumah Sakit -->
        <div class="field-group">
          <label class="field-label">Rumah Sakit <span class="required">*</span></label>
          <div v-for="(_, idx) in form.hospitalList" :key="'rs-' + idx" class="list-row">
            <a-input
              :value="form.hospitalList[idx]"
              placeholder="Masukkan Nama Rumah Sakit"
              class="field-input"
              @change="e => (form.hospitalList[idx] = e.target.value)"
            />
            <button class="btn-icon-only" @click="removeHospital(idx)">
              <delete-outlined />
            </button>
          </div>
          <button v-if="form.hospitalList.length < 3" class="btn-tambah" @click="addHospital">
            <plus-outlined /> TAMBAH
          </button>
        </div>

        <!-- Riwayat Pendidikan -->
        <div class="field-group">
          <label class="field-label">Riwayat Pendidikan <span class="required">*</span></label>
          <div v-for="(edu, idx) in form.educations" :key="'edu-' + idx" class="edu-entry">
            <div class="edu-entry-header">
              <span class="edu-entry-num">Pendidikan {{ idx + 1 }}</span>
              <button class="btn-icon-only" @click="removeEducation(idx)"><delete-outlined /></button>
            </div>
            <div class="field-sublabel">Nama Fakultas / Universitas</div>
            <a-input
              :value="edu.asalFakultas"
              placeholder="Contoh: FK Universitas Indonesia"
              class="field-input"
              @change="e => (form.educations[idx].asalFakultas = e.target.value)"
            />
            <div class="edu-year-row">
              <div class="edu-year-col">
                <div class="field-sublabel">Tahun Masuk</div>
                <a-select
                  :value="edu.tahunMasuk || undefined"
                  placeholder="Pilih tahun"
                  class="field-select"
                  :options="yearOptions"
                  allow-clear
                  @change="val => (form.educations[idx].tahunMasuk = val || 0)"
                />
              </div>
              <div class="edu-year-col">
                <div class="field-sublabel">Tahun Lulus</div>
                <a-select
                  :value="edu.tahunLulus || undefined"
                  placeholder="Pilih tahun"
                  class="field-select"
                  :options="yearOptions"
                  allow-clear
                  @change="val => (form.educations[idx].tahunLulus = val || 0)"
                />
              </div>
            </div>
          </div>
          <button v-if="form.educations.length < 3" class="btn-tambah" @click="addEducation">
            <plus-outlined /> TAMBAH
          </button>
        </div>

        <!-- Foto Profil -->
        <div class="field-group">
          <label class="field-label">Foto Profil Formal <span class="required">*</span></label>
          <div class="avatar-wrapper" @click="triggerAvatarUpload">
            <img v-if="avatarPreview" :src="avatarPreview" alt="avatar" class="avatar-img" />
            <div v-else class="avatar-placeholder">
              <camera-outlined />
              <span>Foto</span>
            </div>
          </div>
          <input
            ref="avatarInput"
            type="file"
            accept="image/*"
            class="hidden-input"
            @change="handleAvatarChange"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-save" :disabled="saving" @click="showSaveConfirm">SIMPAN</button>
        <button class="btn-back" @click="handleClose">KEMBALI</button>
      </div>
    </div>
  </a-modal>

  <!-- Konfirmasi simpan -->
  <a-modal
    v-model:visible="confirmVisible"
    :footer="null"
    :closable="false"
    :width="400"
    centered
    class="confirm-modal"
  >
    <div class="confirm-body">
      <div class="confirm-header">
        <span class="confirm-title">Ubah data?</span>
        <close-outlined class="close-btn" @click="confirmVisible = false" />
      </div>
      <p class="confirm-text">Mohon cek kembali perubahan data sebelum menyimpan formulir.</p>
      <div class="confirm-actions">
        <button class="btn-back full" @click="confirmVisible = false">KEMBALI</button>
        <button class="btn-save full" :disabled="saving" @click="doSave">YA, SIMPAN</button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { notification } from 'ant-design-vue'
import { CloseOutlined, DeleteOutlined, PlusOutlined, CameraOutlined } from '@ant-design/icons-vue'
import { mobileCmsMemberAPI } from '@/services/axios/perki/mobileCms'

const SUBSPECIALTIES = [
  'Prevention & CB',
  'Rehabilitation',
  'Interventional Cardiology',
  'Echocardiography',
  'Nuclear Cardiology & CV Imaging',
  'Arrhytmia & EP',
  'Pediatric Cardiology & CHD',
  'Intensive & Acute CV Care',
  'Vascular Medicine',
  'General Cardiology',
].map(v => ({ label: v, value: v }))

const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: currentYear - 1959 }, (_, i) => currentYear - i).map(y => ({
  label: String(y),
  value: y,
}))

const BASE_FILES_URL = process.env.VUE_APP_PERKI_FILES_URL || 'http://localhost:3000/files'

export default {
  name: 'MemberPerkiEditModal',
  components: { CloseOutlined, DeleteOutlined, PlusOutlined, CameraOutlined },
  props: {
    visible: { type: Boolean, default: false },
    memberId: { type: String, default: null },
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const GELAR_DEPAN_OPTIONS = ['Dr', 'DR', 'Prof', 'Dr. Med']

    const form = ref({
      name: '',
      gelarDepanSelected: [],
      gelarBelakangList: [],
      subspesialisList: [''],
      hospitalList: [''],
      educations: [{ asalFakultas: '', tahunMasuk: 0, tahunLulus: 0 }],
    })
    const avatarPreview = ref(null)
    const avatarFile = ref(null)
    const avatarInput = ref(null)
    const saving = ref(false)
    const confirmVisible = ref(false)

    const subspecialtyOptions = computed(() => SUBSPECIALTIES)
    const yearOptions = computed(() => YEARS)

    const loadMember = async () => {
      if (!props.memberId) return
      try {
        const res = await mobileCmsMemberAPI.getById(props.memberId)
        const m = res.data?.result
        if (!m) return

        const spList = [m.competencyName1, m.competencyName2].filter(Boolean)

        let gelarBelakangList = []
        if (m.gelarBelakang) {
          try {
            const parsed = JSON.parse(m.gelarBelakang)
            if (Array.isArray(parsed)) gelarBelakangList = parsed.filter(Boolean)
            else if (m.gelarBelakang) gelarBelakangList = [m.gelarBelakang]
          } catch {
            if (m.gelarBelakang) gelarBelakangList = [m.gelarBelakang]
          }
        }

        const rawGelarDepan = m.gelarDepan || ''
        const gelarDepanSelected = GELAR_DEPAN_OPTIONS.filter(opt =>
          rawGelarDepan.split(' ').includes(opt),
        )

        const educations =
          Array.isArray(m.educations) && m.educations.length
            ? m.educations.map(e => ({
                asalFakultas: e.asalFakultas || '',
                tahunMasuk: e.tahunMasuk || 0,
                tahunLulus: e.tahunLulus || 0,
              }))
            : m.university || m.graduationYear
            ? [{ asalFakultas: m.university || '', tahunMasuk: m.tahunMasuk || 0, tahunLulus: m.graduationYear || 0 }]
            : [{ asalFakultas: '', tahunMasuk: 0, tahunLulus: 0 }]

        form.value = {
          name: m.name || '',
          gelarDepanSelected,
          gelarBelakangList,
          subspesialisList: spList.length ? spList : ['General Cardiology'],
          hospitalList: [m.hospital1, m.hospital2, m.hospital3].filter(Boolean),
          educations,
        }
        if (form.value.hospitalList.length === 0) form.value.hospitalList = ['']

        if (m.avatar) {
          const av = m.avatar
          if (av.startsWith('http') || av.startsWith('/')) {
            avatarPreview.value = av.startsWith('/')
              ? `${BASE_FILES_URL.replace(/\/files$/, '')}${av}`
              : av
          } else {
            avatarPreview.value = `${BASE_FILES_URL}/members/${av}`
          }
        } else if (m.fileFotoProfil) {
          avatarPreview.value = `${BASE_FILES_URL}/membership-apps/${m.fileFotoProfil}`
        } else {
          avatarPreview.value = null
        }
      } catch {
        notification.error({ message: 'Error', description: 'Gagal memuat data member.' })
      }
    }

    watch(
      () => props.visible,
      v => {
        if (v) {
          loadMember()
          avatarFile.value = null
          confirmVisible.value = false
        }
      },
    )

    const toggleGelarDepan = opt => {
      const list = form.value.gelarDepanSelected
      const idx = list.indexOf(opt)
      if (idx >= 0) list.splice(idx, 1)
      else list.push(opt)
    }

    const addGelarBelakang = () => form.value.gelarBelakangList.push('')
    const removeGelarBelakang = idx => {
      form.value.gelarBelakangList.splice(idx, 1)
    }

    const addSubspesialis = () => {
      if (form.value.subspesialisList.length < 2) form.value.subspesialisList.push('')
    }
    const removeSubspesialis = idx => {
      form.value.subspesialisList.splice(idx, 1)
      if (form.value.subspesialisList.length === 0) form.value.subspesialisList.push('')
    }
    const addHospital = () => {
      if (form.value.hospitalList.length < 3) form.value.hospitalList.push('')
    }
    const removeHospital = idx => {
      form.value.hospitalList.splice(idx, 1)
      if (form.value.hospitalList.length === 0) form.value.hospitalList.push('')
    }
    const addEducation = () => {
      if (form.value.educations.length < 3)
        form.value.educations.push({ asalFakultas: '', tahunMasuk: 0, tahunLulus: 0 })
    }
    const removeEducation = idx => {
      form.value.educations.splice(idx, 1)
      if (form.value.educations.length === 0)
        form.value.educations.push({ asalFakultas: '', tahunMasuk: 0, tahunLulus: 0 })
    }
    const triggerAvatarUpload = () => avatarInput.value?.click()
    const handleAvatarChange = e => {
      const file = e.target.files?.[0]
      if (!file) return
      avatarFile.value = file
      avatarPreview.value = URL.createObjectURL(file)
    }

    const showSaveConfirm = () => {
      if (!form.value.name.trim()) {
        notification.warning({ message: 'Peringatan', description: 'Nama lengkap wajib diisi.' })
        return
      }
      confirmVisible.value = true
    }

    const doSave = async () => {
      saving.value = true
      try {
        const hospitals = [...form.value.hospitalList, '', '', '']
        const gelarBelakangClean = form.value.gelarBelakangList.filter(g => g.trim())
        const payload = {
          name: form.value.name,
          gelarDepan: form.value.gelarDepanSelected.length
            ? form.value.gelarDepanSelected.join(' ')
            : null,
          gelarBelakang: gelarBelakangClean.length ? JSON.stringify(gelarBelakangClean) : null,
          competencyName1: form.value.subspesialisList[0] || null,
          competencyName2: form.value.subspesialisList[1] || null,
          hospital1: hospitals[0] || null,
          hospital2: hospitals[1] || null,
          hospital3: hospitals[2] || null,
          educations: form.value.educations.filter(e => e.asalFakultas.trim()),
        }
        await mobileCmsMemberAPI.update(props.memberId, payload)

        if (avatarFile.value) {
          const fd = new FormData()
          fd.append('avatar', avatarFile.value)
          await mobileCmsMemberAPI.uploadAvatar(props.memberId, fd)
        }

        notification.success({
          message: 'Success',
          description: 'Member berhasil diubah.',
          placement: 'topRight',
          class: 'premium-notification',
          duration: 3,
        })
        confirmVisible.value = false
        emit('saved')
      } catch {
        notification.error({ message: 'Error', description: 'Gagal menyimpan data member.' })
      } finally {
        saving.value = false
      }
    }

    const handleClose = () => {
      confirmVisible.value = false
      emit('close')
    }

    return {
      form,
      avatarPreview,
      avatarFile,
      avatarInput,
      saving,
      confirmVisible,
      subspecialtyOptions,
      yearOptions,
      gelarDepanOptions: GELAR_DEPAN_OPTIONS,
      toggleGelarDepan,
      addGelarBelakang,
      removeGelarBelakang,
      addSubspesialis,
      removeSubspesialis,
      addHospital,
      removeHospital,
      addEducation,
      removeEducation,
      triggerAvatarUpload,
      handleAvatarChange,
      showSaveConfirm,
      doSave,
      handleClose,
    }
  },
}
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 4px 0;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-family: 'Lato', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #000;
  line-height: 28px;
}

.close-btn {
  font-size: 20px;
  cursor: pointer;
  color: #444;
}

.close-btn:hover {
  color: #e10f28;
}

/* Form fields */
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #444;
  line-height: 22px;
}

.field-sublabel {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #798892;
  margin-bottom: 4px;
}

.edu-year-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.edu-year-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.edu-entry {
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.edu-entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.edu-entry-num {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #282828;
}

.required {
  color: #dc3545;
}

.field-input :deep(.ant-input),
.field-input {
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  height: 32px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #444;
}

.field-input :deep(.ant-input::placeholder),
.field-input::placeholder {
  color: #798892;
}

.field-select :deep(.ant-select-selection-placeholder),
.field-select-year :deep(.ant-select-selection-placeholder) {
  color: #798892;
}

.field-select {
  flex: 1;
  min-width: 0;
}

.field-select :deep(.ant-select-selector) {
  border: 1px solid #e4e9f0 !important;
  border-radius: 6px !important;
  height: 32px !important;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

.field-select-year {
  width: 140px;
  flex-shrink: 0;
}

.field-select-year :deep(.ant-select-selector) {
  border: 1px solid #e4e9f0 !important;
  border-radius: 6px !important;
  height: 32px !important;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

.list-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-icon-only {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #444;
  font-size: 16px;
  padding: 4px 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.btn-icon-only:hover {
  color: #e10f28;
}

.btn-icon-only.invisible {
  visibility: hidden;
}

.btn-tambah {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  font-family: 'Barlow', 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #282828;
  padding: 8px;
  border-radius: 50px;
}

.btn-tambah:hover {
  color: #e10f28;
}

/* Gelar depan chips */
.gelar-depan-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gelar-depan-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px 5px 8px;
  border-radius: 50px;
  border: 1px solid #e4e9f0;
  background: #fff;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #798892;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  line-height: 20px;
  user-select: none;
}

.gelar-depan-chip:hover {
  border-color: #bbb;
  color: #444;
}

.gelar-depan-chip.selected {
  border-color: #e10f28;
  background: #fff5f6;
  color: #e10f28;
  font-weight: 700;
}

.chip-check {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid #d0d5dd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}

.gelar-depan-chip.selected .chip-check {
  background: #e10f28;
  border-color: #e10f28;
}

/* Avatar */
.avatar-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px dashed #e4e9f0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #798892;
  font-size: 12px;
}

.avatar-placeholder .anticon {
  font-size: 24px;
}

.hidden-input {
  display: none;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e10f28;
  border: 2px solid #e10f28;
  border-radius: 50px;
  color: #fff;
  font-family: 'Barlow', 'Lato', sans-serif;
  font-weight: 700;
  font-size: 14px;
  padding: 8px 16px;
  min-height: 40px;
  cursor: pointer;
  line-height: 22px;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 2px solid #282828;
  border-radius: 50px;
  color: #282828;
  font-family: 'Barlow', 'Lato', sans-serif;
  font-weight: 700;
  font-size: 14px;
  padding: 8px 16px;
  min-height: 40px;
  cursor: pointer;
  line-height: 22px;
}

.btn-back:hover {
  border-color: #e10f28;
  color: #e10f28;
}

.btn-save.full,
.btn-back.full {
  flex: 1;
  justify-content: center;
}

/* Confirm modal */
.confirm-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 4px 0;
}

.confirm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.confirm-title {
  font-family: 'Lato', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #062d4b;
  line-height: 28px;
}

.confirm-text {
  font-family: 'Plus Jakarta Sans', 'Lato', sans-serif;
  font-size: 14px;
  color: #062d4b;
  line-height: 22px;
  margin: 0;
}

.confirm-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
